/**
 * useQuery hook for the rendered HTML content of a single story in the
 * SetupPDF preview pane.
 *
 * Steps:
 *   1. Fetch raw story content via Hasura GraphQL
 *   2. Fetch merge tags via the `getProposalsInStory` REST endpoint
 *   3. Apply merge tags, run `getFullPageContent` + `generateHTML`
 *   4. Return `{ html, storyCustomCss }`
 *
 * The new flow keeps the same steps but runs inside a TanStack `queryFn`.
 * Step 2 is a plain cookie-auth REST POST (inlined from the retired
 * `storyViewEffects.getProposalsInStory`); only `mergeTags` is consumed here.
 *
 * Query key: `['publicationSetupPdf', 'storyHtml', storyId]`.
 * `progressTrackerSettings` is intentionally excluded from the key (matches
 * the original effect, which only re-ran on `selectedStoryId` change).
 *
 * Pass `null` to disable (e.g. when TOC node is selected).
 */

// `useQuery` used directly because the queryFn is a multi-step composition
// (Hasura fetch + REST merge tags + HTML generation) that can't be expressed
// as a single URL fetch. `useFetchQuery` from @opengov/bnp-core excludes
// `queryFn` from its options, so it doesn't support custom async pipelines.
import { useQuery } from '@tanstack/react-query';
import { IMergeTags, IStoryContent, ProgressTrackerSettingType } from '@opengov/stories-shared/types';
import { generateHTML, getFullPageContent } from '@opengov/stories-shared/utils/story';
import { sortAndOrderStoryContents } from '@opengov/stories-shared/utils/general';
import { replaceStoryContentsWithMergeTagValues } from '@opengov/stories-shared/utils/mergeTag';
import { getStoryDetailsForSetupPDF as getStoryDetailsForSetupPDFQuery } from '../../../../graphQL/queries/publicationsQueries';
import { postHasuraGraphql } from '../../../../utils/hasuraGraphql';
import { postJson } from '../../../../utils/fetchUtils';

export type ISetupPDFStoryContent = {
  contents: IStoryContent[];
  title: string;
  description: string;
  customCssCode: string | null;
  _id: string;
  entityId: number;
  settings: { fontsHeadline: string; fontsParagraph: string };
  folder: { publication: { customCssCode: string } };
  isFollowable: boolean;
};

export type IStoryDetailsForSetupPDF = {
  html: string;
  storyCustomCss: string;
};
import { publicationSetupPdfQueryKeys } from './publicationSetupPdfQueryKeys';

type IStoryDetailsQueryData = { storyData: ISetupPDFStoryContent };

export async function fetchStoryDetailsForSetupPdf(storyId: string): Promise<ISetupPDFStoryContent> {
  const data = await postHasuraGraphql<IStoryDetailsQueryData>(
    getStoryDetailsForSetupPDFQuery,
    { id: storyId }
  );
  return data.storyData;
}

async function fetchStoryHtmlContent(
  storyId: string,
  progressTrackerSettings: ProgressTrackerSettingType[] | undefined
): Promise<IStoryDetailsForSetupPDF> {
  const storyContents = await fetchStoryDetailsForSetupPdf(storyId);
  const { title, description, customCssCode, contents, _id, entityId } = storyContents;
  let sortedStoryContents = sortAndOrderStoryContents(contents);

  const proposalsResult = await postJson<{ mergeTags?: IMergeTags }>(
    '/api/stories/v1/stories/getProposalsInStory',
    { story_id: storyId, entity_id: entityId }
  );
  sortedStoryContents = replaceStoryContentsWithMergeTagValues(sortedStoryContents, proposalsResult?.mergeTags);

  const isStoryWithProgressTracker = sortedStoryContents.some(
    (content) => Array.isArray(content.contentChartConfig) && content.contentChartConfig.length > 0
  );
  const { content, containers } = getFullPageContent(
    sortedStoryContents,
    isStoryWithProgressTracker,
    entityId,
    progressTrackerSettings
  );

  const html = generateHTML({
    id: _id,
    title,
    description,
    fonts: [],
    content,
    hasGallery: true,
    isForCustomCssInspector: true,
    options: {},
    isFollowable: false,
    isStoryWithProgressTracker,
    containers,
    addIgnorePortalScript: true
  });
  return { html, storyCustomCss: customCssCode || '' };
}

export type UseStoryHtmlContentParams = {
  storyId: string;
  progressTrackerSettings: ProgressTrackerSettingType[] | undefined;
} | null;

export function useStoryHtmlContent(params: UseStoryHtmlContentParams) {
  return useQuery<IStoryDetailsForSetupPDF>({
    queryKey: params
      ? publicationSetupPdfQueryKeys.storyHtml(params.storyId)
      : ([...publicationSetupPdfQueryKeys.all, 'storyHtml', 'disabled'] as const),
    queryFn: () => fetchStoryHtmlContent(params!.storyId, params!.progressTrackerSettings),
    enabled: params !== null
  });
}
